import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import DOMPurify from 'dompurify';

import type { APIData, Service } from 'webapp-types';
import type { ActivityLogEvent } from 'webapp/enums/activity-log';
import { ActivityLogObjectType, TrackingEventGroup } from 'webapp/enums/activity-log';
import { getPrettyModelName } from 'webapp/utils/model';
import { getFormattedTime, getRelativeTime } from 'webapp/utils/number';

import type Board from 'webapp/models-custom/board';
import type BoardLoop from 'webapp/models-custom/board-loop';
import type Report from 'webapp/models-custom/report';
import Serviced from 'webapp/models-custom/serviced';

type NewActivityLogKeys = ['objectId', 'userId'];
type SavedActivityLogKeys = ['id', 'created', 'trackingEventGroup'];

export type ActivityLogObject = {
	[Property in NewActivityLogKeys[number] as Property]: number;
} & {
	[Property1 in SavedActivityLogKeys[number] as Property1]?: number | null;
} & {
	objectType: ActivityLogObjectType;
	eventType: ActivityLogEvent;
	message?: string | null;
	uniqueReferenceId?: string | null;
	isSystem?: boolean;
	spaceId?: number | null;
	accountName?: string | null;
	isDeleted?: boolean;
	user?: APIData.Settings.ActivityLogUser | null;
};

export default class ActivityLog extends Serviced {
	@service('request') declare requestService: Service.Request;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/reports') declare mmReportsService: Service.ModelManager.Reports;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('model-manager/spaces') declare mmSpacesService: Service.ModelManager.Spaces;
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('timer') declare timerService: Service.Timer;

	@tracked id: number | null;
	@tracked objectType: ActivityLogObjectType;
	@tracked objectId: number;
	@tracked userId: number;
	@tracked eventType: ActivityLogEvent;
	@tracked created: number | null;
	@tracked message: string | null;
	@tracked uniqueReferenceId: string | null;
	@tracked trackingEventGroup: TrackingEventGroup | null;
	@tracked isSystem: boolean;
	@tracked spaceId: number | null;
	@tracked accountName: string;
	@tracked isAccountDeleted: boolean;
	@tracked eventUser: APIData.Settings.ActivityLogUser | null;

	constructor(model: ActivityLogObject) {
		super();

		this.objectType = model.objectType;
		this.objectId = model.objectId;
		this.userId = model.userId;
		this.eventType = model.eventType;
		this.id = model.id || null;
		this.created = model.created || null;
		this.message = model.message || null;
		this.uniqueReferenceId = model.uniqueReferenceId || null;
		this.trackingEventGroup = model.trackingEventGroup || null;
		this.isSystem = model.isSystem ?? false;
		this.spaceId = model.spaceId ?? null;
		this.accountName = model.accountName ?? '';
		this.isAccountDeleted = model.isDeleted ?? false;
		this.eventUser = model.user ?? null;
	}

	get isNew(): boolean {
		return !this.id;
	}

	get isEmail(): boolean {
		return this.trackingEventGroup === TrackingEventGroup.EMAIL;
	}

	get relativeTime(): string {
		this.timerService.observe();
		const created = this.created;

		if (created) {
			return getRelativeTime(created);
		} else {
			return '-';
		}
	}

	@cached
	get user(): { name: string; avatar?: string } | undefined {
		const user = this.mmUsersService.findModel(String(this.userId));
		if (user) {
			return user;
		}

		const eventUser = this.eventUser;
		if (eventUser) {
			return { name: eventUser.name ?? '', avatar: eventUser.avatar ?? undefined };
		}

		return undefined;
	}

	get fullTime(): string {
		return this.created
			? getFormattedTime(
					this.created,
					this.currentUserService.model?.timezone,
					'MMM D, YYYY, h:mm A',
				)
			: '';
	}

	get isCurrentAccount(): boolean {
		return !this.spaceId || this.spaceId === this.accountService.id;
	}

	get accountColor(): string | undefined {
		return this.spaceId
			? this.mmSpacesService.findRecord(String(this.spaceId))?.color
			: undefined;
	}

	get sanitizedMessage() {
		const message = this.message;
		if (message) {
			return DOMPurify.sanitize(message, {
				ALLOWED_TAGS: ['b'],
				ALLOWED_ATTR: [],
			});
		} else {
			return '';
		}
	}

	get object(): Board | BoardLoop | Report | undefined {
		let object,
			models: (Board | BoardLoop | Report)[] = [];

		switch (this.objectType) {
			case ActivityLogObjectType.BOARD:
				models = this.mmBoardsService.normalBoardModels;
				break;
			case ActivityLogObjectType.BOARD_LOOP:
				models = this.mmBoardLoopsService.normalBoardLoopModels;
				break;
			case ActivityLogObjectType.REPORT:
				models = this.mmReportsService.normalReportModels;
				break;
			default:
				break;
		}

		if (models.length && this.objectId) {
			const objectId = +this.objectId;
			object = models.find((o) => o.id && o.numericId === objectId);
		}

		return object;
	}

	get modelTypeName() {
		return getPrettyModelName(this.object);
	}
}
